(() => {
  const disclosure = document.querySelector('[data-quiz-disclosure]');
  if (!disclosure) return;
  const mobile = window.matchMedia('(max-width: 760px)');
  let mobileOpen = false;

  function syncLayout() {
    disclosure.dataset.quizLayout = mobile.matches ? 'mobile' : 'desktop';
    disclosure.open = mobile.matches ? mobileOpen : true;
  }

  disclosure.addEventListener('toggle', () => {
    if (mobile.matches) mobileOpen = disclosure.open;
    else if (!disclosure.open) disclosure.open = true;
  });
  mobile.addEventListener('change', syncLayout);
  syncLayout();
})();

export const QUIZ_LENGTH = 8;

export function shuffleQuizItems(items, random = Math.random) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const swap = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swap]] = [shuffled[swap], shuffled[index]];
  }
  return shuffled;
}

export function selectQuizQuestions(pool, previousIds = [], random = Math.random) {
  const unique = [...new Map(pool.map(question => [question.id, question])).values()];
  if (unique.length < QUIZ_LENGTH) throw new Error('Eight unique questions are required');
  const previous = new Set(previousIds);
  const fresh = shuffleQuizItems(unique.filter(question => !previous.has(question.id)), random);
  const recent = shuffleQuizItems(unique.filter(question => previous.has(question.id)), random);
  return [...fresh, ...recent].slice(0, QUIZ_LENGTH).map(question => {
    const order = shuffleQuizItems(question.options.map((_, index) => index), random);
    return {...question, options: order.map(index => question.options[index]), correctIndex: order.indexOf(question.correctIndex)};
  });
}

export function createQuizSession(questions) {
  if (questions.length !== QUIZ_LENGTH) throw new Error('A round must contain exactly eight questions');
  return {questions, index: 0, answers: [], complete: false};
}

export function answerQuizQuestion(state, selectedIndex) {
  if (state.complete || state.answers[state.index]) return state;
  const question = state.questions[state.index];
  if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || selectedIndex >= question.options.length) return state;
  return {...state, answers: [...state.answers, {selectedIndex, correct: selectedIndex === question.correctIndex}]};
}

export function advanceQuizQuestion(state) {
  if (state.complete || !state.answers[state.index]) return state;
  if (state.index === QUIZ_LENGTH - 1) return {...state, complete: true};
  return {...state, index: state.index + 1};
}

export function quizScore(state) {
  return state.answers.filter(answer => answer.correct).length;
}

(() => {
  const card = document.querySelector('.quiz-card');
  const pool = JSON.parse(document.getElementById('quiz-question-pool').textContent);
  const heading = card.querySelector('#quiz-question-title');
  const options = card.querySelector('.quiz-options');
  const feedback = card.querySelector('.quiz-feedback p');
  const next = card.querySelector('[data-quiz-next]');
  const questionPanel = card.querySelector('[data-quiz-question]');
  const resultPanel = card.querySelector('[data-quiz-result]');
  const quizBody = card.querySelector('[data-quiz-body]');
  const stage = card.querySelector('[data-quiz-stage]');
  const slide = card.querySelector('[data-quiz-slide]');
  const progress = [...card.querySelectorAll('.quiz-progress li')];
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const desktopLayout = window.matchMedia?.('(min-width: 1100px)');
  const mobileLayout = window.matchMedia?.('(max-width: 760px)');
  const heroCopy = card.closest?.('.hero')?.querySelector('.hero-copy');
  const storageKey = 'kk-preview-last-eight-questions-v1';
  let previousIds = [];
  let state;
  let finishTransition;
  let measuredWidth = 0;
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
    if (Array.isArray(stored)) previousIds = stored.filter(id => typeof id === 'string').slice(0, QUIZ_LENGTH);
  } catch {}

  function setFeedback(title, explanation) {
    const detail = document.createElement('small');
    detail.textContent = explanation;
    feedback.replaceChildren(document.createTextNode(title), detail);
  }

  function updateProgress() {
    progress.forEach((bar, index) => {
      const answer = state.answers[index];
      const active = !state.complete && index === state.index;
      bar.dataset.state = answer ? (answer.correct ? 'correct' : 'wrong') : active ? 'current' : 'pending';
      bar.setAttribute('aria-label', `Frage ${index + 1}: ${answer ? (answer.correct ? 'richtig beantwortet' : 'falsch beantwortet') : active ? 'aktuell' : 'noch offen'}`);
      if (active) bar.setAttribute('aria-current', 'step');
      else bar.removeAttribute('aria-current');
    });
    card.querySelector('[data-quiz-counter]').textContent = `${String(state.index + 1).padStart(2, '0')} / 08`;
  }

  function focusQuestion(target) {
    target.focus({preventScroll: true});
  }

  function optionButtons(question, interactive = true) {
    return question.options.map((text, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.quizChoice = String(index);
      const letter = document.createElement('b');
      letter.textContent = String.fromCharCode(65 + index);
      letter.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.textContent = text;
      const status = document.createElement('i');
      status.setAttribute('aria-hidden', 'true');
      button.append(letter, label, status);
      if (interactive) button.addEventListener('click', () => chooseAnswer(index));
      else button.tabIndex = -1;
      return button;
    });
  }

  function presentationCopy(className) {
    const copy = slide.cloneNode(true);
    copy.removeAttribute('data-quiz-slide');
    copy.classList.add(className);
    copy.inert = true;
    copy.setAttribute('aria-hidden', 'true');
    copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
    copy.querySelectorAll('[aria-live]').forEach(node => node.removeAttribute('aria-live'));
    copy.querySelectorAll('[aria-labelledby]').forEach(node => node.removeAttribute('aria-labelledby'));
    copy.querySelectorAll('button, a, [tabindex]').forEach(node => node.tabIndex = -1);
    return copy;
  }

  function reserveHeight(force = false) {
    if (!stage || !slide) return;
    if (mobileLayout?.matches) {
      // Mobile has a compact, stable reading area instead of reserving space
      // for the longest explanation in the entire question pool.
      card.style.removeProperty('--quiz-desktop-height');
      stage.style.removeProperty('--quiz-stage-height');
      measuredWidth = 0;
      return;
    }
    const peerHeight = desktopLayout?.matches ? heroCopy?.getBoundingClientRect().height : 0;
    if (peerHeight > 0) {
      // Desktop follows the copy beside it; long content scrolls inside the card.
      card.style.setProperty('--quiz-desktop-height', `${Math.ceil(peerHeight)}px`);
      return;
    }
    card.style.removeProperty('--quiz-desktop-height');
    const width = stage.getBoundingClientRect().width;
    // A closed mobile disclosure cannot be measured until it opens.
    if (!width || (!force && Math.abs(width - measuredWidth) < 1)) return;
    measuredWidth = width;
    const copy = presentationCopy('quiz-slide--measure');
    const copyQuestion = copy.querySelector('[data-quiz-question]');
    const copyResult = copy.querySelector('[data-quiz-result]');
    copyQuestion.hidden = false;
    copyResult.hidden = true;
    stage.append(copy);
    let height = 0;
    try {
      // Measure original content at the current font size and width, including
      // feedback. The whole pool keeps later rounds just as stable as this one.
      for (const question of pool) {
        copy.querySelector('[data-quiz-topic]').textContent = question.level + ' · ' + question.learningField;
        copyQuestion.querySelector('h2').textContent = question.prompt;
        copyQuestion.querySelector('.quiz-options').replaceChildren(...optionButtons(question, false));
        const explanation = document.createElement('small');
        explanation.textContent = question.explanation;
        copyQuestion.querySelector('.quiz-feedback p').replaceChildren(document.createTextNode('Nicht ganz. Die richtige Antwort ist markiert.'), explanation);
        height = Math.max(height, copy.getBoundingClientRect().height);
      }
      copyQuestion.hidden = true;
      copyResult.hidden = false;
      copy.querySelector('[data-quiz-topic]').textContent = 'Runde abgeschlossen';
      copy.querySelector('[data-quiz-score]').textContent = '8 von 8 richtig';
      copy.querySelector('[data-quiz-result-message]').textContent = 'Genau dafür ist Üben da. Mit jeder Erklärung wird der nächste Zusammenhang klarer.';
      height = Math.max(height, copy.getBoundingClientRect().height);
      stage.style.setProperty('--quiz-stage-height', `${Math.ceil(height) + 2}px`);
    } finally {
      copy.remove();
    }
  }

  function changeView(render) {
    if (finishTransition) return;
    reserveHeight();
    const canAnimate = slide && typeof slide.animate === 'function' && !reducedMotion?.matches && stage.getBoundingClientRect().width > 0;
    const previousScroll = quizBody?.scrollTop || 0;
    const previousResultScroll = resultPanel.scrollTop || 0;
    const previous = canAnimate ? presentationCopy('quiz-slide--outgoing') : null;
    render();
    const focusNewQuestion = () => focusQuestion(state.complete ? card.querySelector('#quiz-result-title') : heading);
    if (!previous) {
      focusNewQuestion();
      return;
    }
    stage.append(previous);
    // Scroll offsets must be restored after the copy is attached and laid out.
    const previousBody = previous.querySelector('[data-quiz-body]');
    if (previousBody) previousBody.scrollTop = previousScroll;
    previous.querySelector('[data-quiz-result]').scrollTop = previousResultScroll;
    stage.dataset.transitioning = 'true';
    stage.setAttribute('aria-busy', 'true');
    slide.inert = true;
    const animations = [];
    let finished = false;
    finishTransition = () => {
      if (finished) return;
      finished = true;
      previous.remove();
      animations.forEach(animation => animation.cancel());
      slide.inert = false;
      delete stage.dataset.transitioning;
      stage.removeAttribute('aria-busy');
      finishTransition = undefined;
      // Do not pull focus back if the user has tabbed out or closed the example.
      if (!document.hidden && stage.getBoundingClientRect().width > 0 &&
          (!document.activeElement || document.activeElement === document.body || card.contains(document.activeElement))) focusNewQuestion();
    };
    try {
      const timing = {duration: 420, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'};
      const animate = (element, frames) => {
        const animation = element.animate(frames, timing);
        animations.push(animation);
        return animation.finished.catch(() => {});
      };
      const outgoing = animate(previous, [{transform: 'translateX(0)', opacity: 1}, {transform: 'translateX(-105%)', opacity: 0}]);
      const incoming = animate(slide, [{transform: 'translateX(105%)', opacity: .35}, {transform: 'translateX(0)', opacity: 1}]);
      const finish = finishTransition;
      Promise.all([outgoing, incoming]).then(finish);
    } catch {
      finishTransition();
    }
  }

  function renderQuestion() {
    const question = state.questions[state.index];
    card.dataset.questionId = question.id;
    card.dataset.quizState = 'question';
    questionPanel.hidden = false;
    resultPanel.hidden = true;
    heading.textContent = question.prompt;
    const topic = card.querySelector('[data-quiz-topic]');
    topic.textContent = question.level + ' · ' + question.learningField;
    topic.title = question.learningField + ' · ' + question.chapter;
    options.replaceChildren(...optionButtons(question));
    setFeedback('Tippe auf eine Antwort.', 'Danach bekommst du die Erklärung direkt dazu.');
    next.disabled = true;
    next.textContent = state.index === QUIZ_LENGTH - 1 ? 'Ergebnis ansehen' : 'Nächste Frage';
    updateProgress();
    if (quizBody) quizBody.scrollTop = 0;
  }

  function chooseAnswer(index) {
    if (finishTransition) return;
    const updated = answerQuizQuestion(state, index);
    if (updated === state) return;
    state = updated;
    const question = state.questions[state.index];
    const answer = state.answers[state.index];
    [...options.children].forEach((button, optionIndex) => {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-pressed', String(optionIndex === index));
      if (optionIndex === question.correctIndex) {
        button.classList.add('correct');
        button.querySelector('i').textContent = '';
        button.querySelector('i').dataset.kkAnswerIcon = 'check';
        button.setAttribute('aria-label', question.options[optionIndex] + ' – richtige Antwort');
      } else if (optionIndex === index) {
        button.classList.add('wrong');
        button.querySelector('i').textContent = '';
        button.querySelector('i').dataset.kkAnswerIcon = 'close';
        button.setAttribute('aria-label', question.options[optionIndex] + ' – deine Antwort, nicht richtig');
      }
    });
    setFeedback(answer.correct ? 'Richtig!' : 'Nicht ganz. Die richtige Antwort ist markiert.', question.explanation);
    next.disabled = false;
    updateProgress();
  }

  function showResult() {
    card.dataset.quizState = 'complete';
    questionPanel.hidden = true;
    resultPanel.hidden = false;
    resultPanel.scrollTop = 0;
    card.querySelector('[data-quiz-topic]').textContent = 'Runde abgeschlossen';
    card.querySelector('[data-quiz-score]').textContent = `${quizScore(state)} von 8 richtig`;
    card.querySelector('[data-quiz-result-message]').textContent = quizScore(state) === QUIZ_LENGTH
      ? 'Stark! Alle acht Antworten sitzen. In der App wartet der nächste Schritt.'
      : 'Genau dafür ist Üben da. Mit jeder Erklärung wird der nächste Zusammenhang klarer.';
    updateProgress();
  }

  function startRound() {
    const questions = selectQuizQuestions(pool, previousIds);
    previousIds = questions.map(question => question.id);
    try { sessionStorage.setItem(storageKey, JSON.stringify(previousIds)); } catch {}
    state = createQuizSession(questions);
    renderQuestion();
  }

  next.addEventListener('click', () => {
    if (finishTransition) return;
    const updated = advanceQuizQuestion(state);
    if (updated === state) return;
    changeView(() => {
      state = updated;
      if (state.complete) showResult();
      else renderQuestion();
    });
  });
  card.querySelector('[data-quiz-restart]').addEventListener('click', () => changeView(startRound));
  startRound();
  reserveHeight();
  if (stage) {
    const remeasure = () => {
      finishTransition?.();
      reserveHeight(true);
    };
    let resizePending = false;
    window.addEventListener?.('resize', () => {
      if (resizePending) return;
      resizePending = true;
      const run = () => { resizePending = false; remeasure(); };
      if (window.requestAnimationFrame) window.requestAnimationFrame(run);
      else run();
    });
    card.closest?.('[data-quiz-disclosure]')?.addEventListener('toggle', remeasure);
    if (typeof window.ResizeObserver === 'function') new window.ResizeObserver(() => reserveHeight()).observe(stage);
    if (heroCopy && typeof window.ResizeObserver === 'function') new window.ResizeObserver(() => reserveHeight(true)).observe(heroCopy);
    document.fonts?.ready.then(remeasure);
    desktopLayout?.addEventListener('change', remeasure);
    mobileLayout?.addEventListener('change', remeasure);
    reducedMotion?.addEventListener('change', () => { if (reducedMotion.matches) finishTransition?.(); });
    document.addEventListener?.('visibilitychange', () => { if (document.hidden) finishTransition?.(); });
  }
})();
